/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import styles from '@app/components/channel/MentionEveryonePopout.module.css';
import {isIMEComposing} from '@app/utils/IMECompositionUtils';
import {getCurrentLocale} from '@app/utils/LocaleUtils';
import {formatNumber} from '@fluxer/number_utils/src/NumberFormatting';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {WarningIcon} from '@phosphor-icons/react';
import {useCallback, useEffect} from 'react';

interface MentionEveryonePopoutProps {
	mentionType: '@everyone' | '@here' | 'role';
	memberCount: number;
	onConfirm: () => void;
	onCancel: () => void;
	roleName?: string;
}

const isMac = () => /Mac|iPod|iPhone|iPad/.test(navigator.platform);

export const getMentionTitle = (
	mentionType: MentionEveryonePopoutProps['mentionType'],
	roleName?: string,
	i18n?: I18n,
) => {
	if (mentionType === 'role') {
		return <Trans>Mention {roleName ?? i18n?._(msg`this role`) ?? 'this role'}?</Trans>;
	}
	if (mentionType === '@everyone') {
		return <Trans>Mention @everyone?</Trans>;
	}
	return <Trans>Mention @here?</Trans>;
};

export const getMentionDescription = (
	mentionType: MentionEveryonePopoutProps['mentionType'],
	memberCount: number,
	roleName?: string,
	i18n?: I18n,
) => {
	if (mentionType === 'role') {
		return (
			<Trans>
				This will notify <strong>{formatNumber(memberCount, getCurrentLocale())}</strong> members with the{' '}
				<span className={styles.roleName}>{roleName ?? i18n?._(msg`mentioned role`) ?? 'mentioned role'}</span> in this
				channel. Are you sure you want to do this?
			</Trans>
		);
	}
	if (mentionType === '@everyone') {
		return (
			<Trans>
				This will notify <strong>{formatNumber(memberCount, getCurrentLocale())}</strong> members in this channel. Are
				you sure you want to do this?
			</Trans>
		);
	}
	return (
		<Trans>
			This will notify up to <strong>{formatNumber(memberCount, getCurrentLocale())}</strong> online members in this
			channel. Are you sure you want to do this?
		</Trans>
	);
};

export const MentionEveryonePopout = ({
	mentionType,
	memberCount,
	onConfirm,
	onCancel,
	roleName,
}: MentionEveryonePopoutProps) => {
	const {i18n} = useLingui();
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				onCancel();
				return;
			}

			if (event.key === 'Enter') {
				if (isIMEComposing(event)) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				onConfirm();
			}
		},
		[onCancel, onConfirm],
	);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown, true);
		return () => document.removeEventListener('keydown', handleKeyDown, true);
	}, [handleKeyDown]);

	const enterKeySymbol = isMac() ? '↵' : 'Enter';

	return (
		<div className={styles.container} role="dialog" aria-modal="true">
			<div className={styles.header}>
				<WarningIcon size={20} weight="fill" className={styles.warningIcon} />
				<span className={styles.title}>{getMentionTitle(mentionType, roleName, i18n)}</span>
			</div>
			<p className={styles.description}>{getMentionDescription(mentionType, memberCount, roleName, i18n)}</p>
			<div className={styles.keybinds}>
				<div className={styles.keybind}>
					<kbd className={styles.keybindHint}>Esc</kbd>
					<span>
						<Trans>Cancel</Trans>
					</span>
				</div>
				<div className={styles.keybind}>
					<kbd className={styles.keybindHint}>{enterKeySymbol}</kbd>
					<span>
						<Trans>Confirm</Trans>
					</span>
				</div>
			</div>
		</div>
	);
};
